import React, { useEffect, useState } from "react";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import { CircularProgress, Tabs, Tooltip } from "@mui/material";
import Tab from "@mui/material/Tab";
import { Box, Typography, Card, Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getJobDetails, jobAction } from "../../redux/actions/jobsActions";
import { camelCase, isEmpty, sortBy, startCase, toLower } from "lodash";
import { ROUTE_COMPLETE_PROFILE, ROUTE_JOB_CONGRATS, ROUTE_BROWSE_JOBS_NEW } from "../../constants/routes";
import { JOB_ACTIONS } from "../../constants/AppVarConstants";
import { Language, ArrowBack } from "@mui/icons-material";

function textToHtml(text: any) {
  const htmlText = text ? text.replace(/\n/g, "<br />") : "";

  return <span dangerouslySetInnerHTML={{ __html: htmlText }} />;
}

const EllipsisTooltip = ({ text }: any) => {
  const [open, setOpen] = useState(false);
  const content = Array.isArray(text) ? text.join(", ") : text || "";

  const handleTooltipToggle = () => {
    setOpen(!open);
  };

  return (
    <Box
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        mt: 1,
      }}
    >
      <Tooltip title={content} arrow open={open} onOpen={() => setOpen(true)} onClose={() => setOpen(false)}>
        <Typography
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: "14px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
          onClick={handleTooltipToggle}
        >
          {content}
        </Typography>
      </Tooltip>
    </Box>
  );
};

const JobDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const routeLocation = useLocation();
  const { jobDetails } = useSelector((state: any) => state.jobs);
  const ajaxCallsInProgress = useSelector((state: any) => state.ajaxCallsInProgress);

  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    dispatch(getJobDetails(params?.id as string));
  }, []);

  const handleTabChange = (event: any, newValue: any) => {
    setSelectedTab(newValue);
  };

  if (ajaxCallsInProgress || isEmpty(jobDetails)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  const {
    companyDetails,
    location,
    title,
    minCtc,
    maxCtc,
    minExperience,
    maxExperience,
    preferredQualification,
    jobCategory,
    jobType,
    tools,
    skills,
    //     specializations,
    workMode,
    functions,
    description,
  } = jobDetails || {};

  const handleJobAction = (jobId: string, action: string) => {
    dispatch(jobAction(jobId, action, () => (action === JOB_ACTIONS.APPLIED ? navigate(ROUTE_JOB_CONGRATS) : {})));
  };

  const openInNewTab = (url: string) => {
    if (!url) return;

    const formattedUrl = url.startsWith("http") ? url : `http://${url}`;
    window.open(formattedUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Box bgcolor="white">
      <Box px={3} pt={2} pb={0} bgcolor="#6352CC">
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center">
            <Button
              onClick={() => {
                const fromBrowseJobs = routeLocation.state?.fromBrowseJobs;
                const fromJobRecommendations = routeLocation.state?.fromJobRecommendations;
                const fromJobApplications = routeLocation.state?.fromJobApplications;
                if (fromJobRecommendations || fromJobApplications) {
                  navigate(ROUTE_COMPLETE_PROFILE, { state: { activeTab: fromJobRecommendations ? 0 : 1 } });
                } else {
                  navigate(ROUTE_BROWSE_JOBS_NEW, { state: { fromJobDetails: true } });
                }
              }}
              sx={{
                minWidth: "auto",
                color: "#fff",
                padding: "8px",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <ArrowBack />
            </Button>
            <img src={companyDetails?.logo} alt="" className="img-fluid" style={{ width: "100px" }} />
            <Box className="ms-3">
              <h4 style={{ color: "#fff", fontSize: "18px" }}>{title}</h4>
              <p style={{ color: "#ADADAD" }}>{companyDetails?.name}</p>
            </Box>
          </Stack>
        </Stack>
        <Box sx={{ overflowX: "auto" }}>
          <Tabs
            variant="scrollable"
            // textColor="#ffffff"
            indicatorColor="primary"
            value={selectedTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTabs-indicator": {
                opacity: "1 !important",
                height: "4px",
                backgroundColor: "#FC794B !important",
                borderRadius: "5px 5px 5px 5px",
                top: 40,
                bottom: "auto",
                transform: "translateY(-100%)",
                width: "100%",
              },
              "& .MuiTab-root": {
                minWidth: "auto",
                padding: "8px 8px",
                fontSize: "14px",
                textTransform: "uppercase",
                color: "#ffffff",
              },
              ".Mui-selected": {
                color: "#fff !important",
                fontWeight: "600",
                border: "none",
                fontSize: "14px",
                textTransform: "uppercase",
              },
            }}
          >
            {["JOB ROLE", "JOB DESCRIPTION", "COMPANY INFO"].map((degree, index) => (
              <Tab key={index} label={degree} />
            ))}
          </Tabs>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1 }} className="mt-1">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            {selectedTab === 0 && (
              <Box
                style={{
                  padding: "20px",
                  background: `#ffffff`,
                  borderRadius: "20px",
                }}
              >
                <Stack direction="row" justifyContent="space-between">
                  <Stack
                    sx={{
                      padding: "10px",
                      width: "85px",
                      direction: "column",
                      alignItems: "left",
                      backgroundColor: "rgba(230, 228, 254, 0.41)",
                      justifyContent: "space-evenly",
                    }}
                  >
                    <img src="/images/complete-profile/Residence.svg" alt="" width="30%" />
                    <EllipsisTooltip text={location} />
                  </Stack>

                  <Stack
                    sx={{
                      padding: "10px",
                      width: "85px",
                      direction: "column",
                      alignItems: "left",
                      backgroundColor: "rgba(230, 228, 254, 0.41)",
                      justifyContent: "space-evenly",
                    }}
                  >
                    <img src="/images/complete-profile/CTC.svg" alt="" width="30%" />
                    <EllipsisTooltip text={`${minCtc} - ${maxCtc}`} />
                  </Stack>

                  <Stack
                    sx={{
                      padding: "10px",
                      width: "85px",
                      direction: "column",
                      alignItems: "left",
                      backgroundColor: "rgba(230, 228, 254, 0.41)",
                      justifyContent: "space-evenly",
                    }}
                  >
                    <img src="/images/complete-profile/Experience.svg" alt="" width="30%" />
                    <EllipsisTooltip text={`${minExperience} - ${maxExperience}`} />
                  </Stack>
                </Stack>

                <Stack direction="row" justifyContent="space-between" className="mt-4">
                  <Stack
                    sx={{
                      padding: "10px",
                      width: "85px",
                      direction: "column",
                      alignItems: "left",
                      backgroundColor: "rgba(230, 228, 254, 0.41)",
                      justifyContent: "space-evenly",
                    }}
                  >
                    <img src="/images/degree.png" alt="" width="30%" />
                    <EllipsisTooltip text={preferredQualification} />
                  </Stack>
                  <Stack
                    sx={{
                      padding: "10px",
                      width: "100px",
                      direction: "column",
                      alignItems: "left",
                      backgroundColor: "rgba(230, 228, 254, 0.41)",
                      justifyContent: "space-evenly",
                    }}
                  >
                    <img src="/images/complete-profile/Job Category.svg" alt="" width="30%" />
                    <EllipsisTooltip text={jobCategory} />
                  </Stack>

                  <Stack
                    sx={{
                      padding: "10px",
                      width: "85px",
                      direction: "column",
                      alignItems: "left",
                      backgroundColor: "rgba(230, 228, 254, 0.41)",
                      justifyContent: "space-evenly",
                    }}
                  >
                    <img src="/images/complete-profile/Job Type.svg" alt="" width="30%" />
                    <EllipsisTooltip text={jobType} />
                  </Stack>

                  </Stack>
                  <Stack direction="row" justifyContent="space-between" className="mt-4">


                  <Stack
                    sx={{
                      padding: "10px",
                      width: "85px",
                      direction: "column",
                      alignItems: "left",
                      backgroundColor: "rgba(230, 228, 254, 0.41)",
                      justifyContent: "space-evenly",
                    }}
                  >
                    <img src="/images/complete-profile/Job Mode.svg" alt="" width="30%" />
                    <EllipsisTooltip text={workMode} />
                  </Stack>
                  {/* Additional job type stack */}
                </Stack>

                {/* Additional job function stack */}

                <Stack direction="row" alignItems="end" className="mt-3">
                  <p style={{ color: "#FE845A", fontSize: "12px", fontWeight: "600" }}>Function</p>
                </Stack>

                <Box className="mt-3">
                  <p style={{ fontSize: "14px" }}>
                    {functions?.map((tool: string, index: number) => (
                      <React.Fragment key={index}>
                        <span>{startCase(toLower(tool))}</span>
                        {index !== functions.length - 1 && <span style={{ color: "orange" }}> | </span>}
                      </React.Fragment>
                    ))}
                  </p>
                </Box>

                <Stack direction="row" alignItems="end" className="mt-3">
                  <p style={{ color: "#FE845A", fontSize: "12px", fontWeight: "600" }}>Key Skills</p>
                </Stack>

                <Box className="mt-3">
                  <p style={{ fontSize: "14px" }}>
                    {skills?.map((tool: string, index: number) => (
                      <React.Fragment key={index}>
                        <span>{startCase(camelCase(tool))}</span>
                        {index !== skills.length - 1 && <span style={{ color: "orange" }}> | </span>}
                      </React.Fragment>
                    ))}
                  </p>
                </Box>
                <Stack direction="row" alignItems="end" className="mt-3">
                  <p style={{ color: "#FE845A", fontSize: "12px", fontWeight: "600" }}>Tools Known</p>
                </Stack>

                <Box className="mt-3">
                  <p style={{ fontSize: "14px" }}>
                    {tools?.map((tool: string, index: number) => (
                      <React.Fragment key={index}>
                        <span>{tool}</span>
                        {index !== tools.length - 1 && <span style={{ color: "orange" }}> | </span>}
                      </React.Fragment>
                    ))}
                  </p>
                </Box>
              </Box>
            )}

            {selectedTab === 1 && (
              <Box
                style={{
                  padding: "15px",
                  background: `#ffffff`,
                  borderRadius: "20px",
                }}
              >
                <Box style={{ borderRadius: "10px", padding: "2px", marginBottom: "10px" }} className="">
                  {sortBy(description, "priority")?.map((el: any) => {
                    return (
                      <Box key={el?.priority}>
                        <Stack
                          style={{
                            backgroundColor: "#e5e3fd",
                            height: "20px",
                            padding: "10px",
                            borderRadius: "10px 10px 0px 0px",
                          }}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}>
                            {el?.header}
                          </Typography>
                        </Stack>
                        <Stack
                          style={{ padding: "10px", marginLeft: el?.header === "Perks" ? "20px" : "" }}
                          direction="column"
                        >
                          {el?.details.split("\n").map((detail: string, index: number) => (
                            <Typography key={index} style={{ color: "#000000", fontSize: "14px", fontWeight: "500" }}>
                              {textToHtml(detail)}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {selectedTab === 2 && (
              <Box
                style={{
                  padding: "20px",
                  background: `#ffffff`,
                  borderRadius: "20px",
                }}
              >
                <Box style={{ borderRadius: "10px", padding: "2px", marginBottom: "10px" }} className="">
                  <Stack
                    style={{
                      backgroundColor: "#e5e3fd",
                      height: "20px",
                      padding: "10px",
                      borderRadius: "10px 10px 0px 0px",
                    }}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}>
                      Company Details
                    </Typography>
                  </Stack>
                  <Stack style={{ padding: "10px" }} direction="column">
                    <Stack direction="column" spacing={1}>
                      <Stack direction="column" spacing={1}>
                        <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                          Company Name: {companyDetails?.name}
                        </Typography>
                      </Stack>
                      <Stack direction="column" spacing={1}>
                        <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                          Company Founded in: {companyDetails?.foundedIn}
                        </Typography>
                      </Stack>
                      <Stack direction="column" spacing={1}>
                        <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                          Number of Employees: {companyDetails?.noOfEmployees}
                        </Typography>
                      </Stack>
                      <Stack direction="column" spacing={1}>
                        <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                          Headquarter in: {companyDetails?.headquarter}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
                      <Language onClick={() => openInNewTab(companyDetails?.website || "")} />
                      <img
                        src="/images/client-website/x-social-media.png"
                        alt="icon"
                        style={{ width: 16, height: 16 }}
                        onClick={() => openInNewTab(companyDetails?.twitterProfile || "")}
                      />
                      <img
                        src="/images/client-website/instagram-original.png"
                        alt="icon"
                        style={{ width: 16, height: 16 }}
                        onClick={() => openInNewTab(companyDetails?.facebookProfile || "")}
                      />
                      <img
                        src="/images/linkedin.png"
                        alt="icon"
                        style={{ width: 30, height: 30 }}
                        onClick={() => openInNewTab(companyDetails?.linkedinProfile || "")}
                      />
                    </Box>
                  </Stack>
                  <Stack
                    style={{
                      backgroundColor: "#e5e3fd",
                      height: "20px",
                      padding: "10px",
                      borderRadius: "10px 10px 0px 0px",
                    }}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}>
                      About Company
                    </Typography>
                  </Stack>
                  <Stack style={{ padding: "10px" }} direction="column">
                    <Stack direction="column" spacing={1}>
                      <Stack direction="column" spacing={1}>
                        {companyDetails?.description.split("\n\n").map((detail: string, index: number) => (
                          <Typography
                            key={index}
                            style={{ color: "#000000", fontSize: "14px", margin: "10px 0px", fontWeight: "500" }}
                          >
                            {textToHtml(detail)}
                          </Typography>
                        ))}
                      </Stack>
                    </Stack>
                  </Stack>
                  <Stack
                    style={{
                      backgroundColor: "#e5e3fd",
                      height: "20px",
                      padding: "10px",
                      borderRadius: "10px 10px 0px 0px",
                    }}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}>
                      Industry That You Operate In
                    </Typography>
                  </Stack>
                  <Box sx={{ padding: "10px 0", display: "flex", flexWrap: "wrap", gap: "10px" }}>
                    {companyDetails?.industryOperateIn.map((detail: string, index: number) => (
                      <Typography
                        key={index}
                        style={{
                          backgroundColor: "#FFF2EE",
                          color: "#000000",
                          fontSize: "12px",
                          fontWeight: "500",
                          padding: "5px 10px",
                          borderRadius: "5px",
                        }}
                      >
                        {detail}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ position: "sticky", bottom: 0, width: "100%", backgroundColor: "white", zIndex: 1 }}>
        {jobDetails?.applied ? null : (
          <Box sx={{ p: 2 }}>
            <Button
              sx={{
                backgroundColor: "#6352CC",
                color: "#fff",
                borderRadius: "99px",
                fontSize: "16px",
                textTransform: "capitalize",
                width: "100%",
                paddingY: "10px",
                paddingX: 0,
                "&:hover": {
                  backgroundColor: "#756DE9",
                },
              }}
              variant="contained"
              size="small"
              className="w-100 btn-pad"
              onClick={() => handleJobAction(jobDetails?.jobId, JOB_ACTIONS.APPLIED)}
            >
              Apply
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default JobDetails;
